import { Paper, Typography, Avatar, Box } from '@mui/material'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import FacetIcon from 'components/core/facets/icons'
import { styles } from 'components/core/facets/styles'
import { C1Icon } from 'components/core/icons/C1Icon'
import { Filter } from 'components/core/search-filter/filter'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { CancelFunc } from 'lib/cancel'
import { MetaFields } from 'lib/search'
import { CircleAlert, ListFilter } from 'lucide-react'
import { FacetValue } from 'pbts/c1/api/search/v1/facet'
import React, { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { TriggerOpts } from 'redux_toolkit/lib/createUnaryApi'

const DropDownItem: React.FC<{ iconUrl: string; displayName: string; count: number; showCount: boolean, id?: string }> = ({
    iconUrl,
    displayName,
    count,
    showCount = true,
    id,
}) => {
    return (
        <>
            {iconUrl && <FacetIcon iconUrl={iconUrl} objectID={id} />}
            <Typography sx={styles.nestedListItemText}>{displayName}</Typography>
            {(showCount && count !== -1) && (
                <Paper sx={sxc(styles.countBox, { marginLeft: 'auto' })} elevation={0} variant="outlined">
                    <Typography variant="caption">
                        {count}
                    </Typography>
                </Paper>
            )}
        </>
    )
}

const Loading: FC = () => (
    <Box>
        <C1CircularProgress />
        <br />
        <FormattedMessage id="loading_items" defaultMessage="Loading items..." />
    </Box>
)

const Empty: FC = () => (
    <Box>
        <Avatar sx={{ height: '25px', width: '25px' }}>
            <C1Icon icon={ListFilter} />
        </Avatar>
        <br />
        <FormattedMessage id="no_items_found" defaultMessage="No items found." />
    </Box>
)

// TODO display error
export const ErrorComponent: FC<{ err: Error }> = ({ err }) => (
    <Box sx={{ color: 'error' }}>
        <Avatar sx={{ color: 'error', backgroundColor: 'grey.50', height: '25px', width: '25px' }}>
            <C1Icon icon={CircleAlert} sx={{ color: 'error.main' }} />
        </Avatar>
        <br />
        {`${err}`}
        <Typography color="error" textAlign="center">
            <FormattedMessage id="no_items_found" defaultMessage="Error loading items." />
        </Typography>
    </Box>
)

type FilterKeys<BaseRequest> = keyof Omit<BaseRequest, MetaFields>
type FilterUpdate<BaseRequest> = Partial<Pick<BaseRequest, FilterKeys<BaseRequest>>>
export type FilterOnChange<BaseRequest> = (updated: FilterUpdate<BaseRequest>) => void

export type StatelyHookReturn = {
    data?: ReadonlyArray<FacetValue>
    isError: boolean
    isLoading: boolean
    error?: Error
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Any = any

export type LazyHook<BaseRequest extends TermRequest<Any>> = () => StatelyHookReturn & {
    trigger: (
        arg: { term: BaseRequest['term']; query: string; search: Search<BaseRequest>; includeCount: boolean },
        opts?: TriggerOpts
    ) => Any
}

export interface TermRequest<Search extends object> {
    search?: Search
    term: number
    query: string
    includeCount: boolean
    injectOption?: FacetValue
}

// TODO: omit meta fields
export type Search<BaseRequest extends TermRequest<Any>> = Exclude<BaseRequest['search'], undefined>
type SearchKey<
    BaseRequest extends TermRequest<Any>,
    K extends keyof Search<BaseRequest>
> = Search<BaseRequest>[K] extends Array<Any> ? K : never

export type FacetFilterProps<BaseRequest extends TermRequest<Any>, K extends keyof Search<BaseRequest>> = {
    hook: LazyHook<BaseRequest>
    term: BaseRequest['term']
    onChange: FilterOnChange<BaseRequest>
    filter: Search<BaseRequest>
    filterKey: SearchKey<BaseRequest, K>
    label: string
    serverSideAutoComplete?: boolean
    showCount?: boolean
    injectOption?: FacetValue
} & Omit<
    React.ComponentProps<typeof Filter<string>>,
    | 'label'
    | 'options'
    | 'renderOption'
    | 'selected'
    | 'onChange'
    | 'filterOptions'
    | 'renderLabel'
    | 'onOpen'
    | 'loading'
    | 'loadingText'
    | 'noOptionsText'
    | 'error'
    | 'errorText'
>

export const PluralFacetFilter = <BaseRequest extends TermRequest<Any>, K extends keyof Search<BaseRequest>>({
    onChange: _onChange,
    hook: useLazyHook,
    filter,
    term,
    filterKey,
    label,
    serverSideAutoComplete = true,
    showCount = true,
    injectOption,
    ...rest
}: FacetFilterProps<BaseRequest, K>): React.ReactElement => {
    const value = filter?.[filterKey] as Array<string> | undefined
    const key = `${String(filterKey)}-${term}`

    const [cachedSelectionMeta, setCachedSelectionMeta] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(key) || '{}')
        } catch {
            return {}
        }
    })

    const { trigger: _trigger, data, isError, isLoading, error } = useLazyHook()
    const [query, setQuery] = useState('')

    const trigger = useCallback(
        () => {
            // Ensure filter has no null values - protobuf expects undefined for missing fields
            const cleanFilter = { ...filter }
            // Remove null values from the filter object
            Object.keys(cleanFilter).forEach((k) => {
                if (cleanFilter[k] === null) {
                    delete cleanFilter[k]
                }
            })
            return _trigger({ term, search: cleanFilter as typeof filter, query, includeCount: true })
        },
        [term, filter, _trigger, query],
    )

    const shouldLoadInitialData = value?.length && isLoading
    const { options, renderLabel, renderOption, onFilterOptions, optionToValue } = useMemo(() => {
        const opts: Array<string> = []
        const optToVal = {}

        if (shouldLoadInitialData) {
            try {
                const o = JSON.parse(localStorage.getItem(key) || '')
                value?.forEach((v) => {
                    const cachedValue = o?.[v]
                    if (!cachedValue?.displayName || !cachedValue?.value) {
                        return
                    }

                    optToVal[v] = o[v]
                    opts.push(v)
                })
            } catch (e) {
                // console.log(e)
            }
        }

        let injectedOptionFound = false
        for (const d of data || []) {
            opts.push(d.value)
            optToVal[d.value] = d
            // check if option is injectOption
            if (injectOption && d.displayName.match(new RegExp(injectOption.displayName, 'gi'))) {
                injectedOptionFound = true
            }
        }

        // if injectOption is not in the list and the user query matches insensitive substring, add it
        if (injectOption && !injectedOptionFound && injectOption.displayName.match(new RegExp(query, 'gi'))) {
            opts.push(injectOption.value)
            optToVal[injectOption.value] = injectOption
        }

        return {
            options: opts,
            optionToValue: optToVal,
            renderLabel: (o: string): string => {
                const labelDisplayName = cachedSelectionMeta[o]?.displayName || optToVal[o]?.displayName || o
                // TODO @degzhaus: remove this after catalog => profile data migration is complete
                const labelStr = String(labelDisplayName)
                return labelStr.toLowerCase() === 'catalog' ? 'Access profile' : labelStr
            },
            renderOption: (_props, option: string): React.ReactElement => {
                if (!option) {
                    return <></>
                }
                const val = optToVal[option] || cachedSelectionMeta[option]
                if (!val) {
                    // TODO @degzhaus: remove this after catalog => profile data migration is complete
                    return <>{option.toLowerCase() === 'catalog' ? 'Access profile' : option}</>
                }
                return <DropDownItem
                    {...val}
                    // TODO @degzhaus: remove this after catalog => profile data migration is complete
                    displayName={val.displayName.toLowerCase() === 'catalog' ? 'Access profile' : val.displayName}
                    id={option}
                    showCount={showCount}
                />
            },
            onFilterOptions: (haystack: string[], needle): Array<string> => {
                if (!needle.inputValue) {
                    return haystack
                }

                needle = needle.inputValue.toLowerCase()
                return data?.filter((x) => x.displayName.toLowerCase().includes(needle))?.map((x) => x.value) || []
            },
        }
    }, [data, isLoading, cachedSelectionMeta, key, shouldLoadInitialData]) // eslint-disable-line react-hooks/exhaustive-deps

    const cachedSelectionUpdate = useCallback((opts: string[], prevState: Record<string, FacetValue>): Record<string, FacetValue> => (
        Object.fromEntries(opts.map((k) => [k, {
            count: 0,
            ...(optionToValue[k] || prevState[k]) || {
                displayName: k,
                value: k,
            },
        }]))
    ), [optionToValue])

    const onChange = useCallback(
        (v: Array<string>): void => {
            if (v?.length === value?.length && v?.every((vv, i) => vv === value[i])) {
                return
            }
            setCachedSelectionMeta((s) => {
                const stuffs = cachedSelectionUpdate(v, s)
                localStorage.setItem(key, JSON.stringify(stuffs))
                return stuffs
            })
            _onChange({ [filterKey]: v } as Partial<BaseRequest>)
        },
        [_onChange, filterKey, key, value, cachedSelectionUpdate],
    )

    const onOptionSelected = (_, opts: string[]): void => {
        setCachedSelectionMeta((s) => cachedSelectionUpdate(opts, s))
    }

    const search = (q: string): CancelFunc => {
        setQuery(q || '')
        return () => { }
    }

    return (
        <Filter<string>
            {...rest}
            label={label}
            options={options}
            renderOption={renderOption}
            selected={value}
            onChange={onChange}
            filterOptions={serverSideAutoComplete ? undefined : onFilterOptions}
            renderLabel={renderLabel}
            onOptionSelected={onOptionSelected}
            onOpen={trigger}
            clearOnClick={false}
            clearOnBlur={false}
            loading={isLoading}
            loadingText={<Loading />}
            noOptionsText={<Empty />}
            error={isError}
            search={search}
            errorText={isError && error && <ErrorComponent err={error} />} // really should be a dependent type
        />
    )
}
